/**
 * Agent settings validation and sanitization against connector schemas.
 *
 * The `connector` table defines `agent_settings_schema` as the source of truth
 * for valid settings per connector type. These utilities ensure settings
 * are valid before persisting to the database.
 */

/**
 * Schema field definition from connector.agent_settings_schema
 */
export interface SchemaField {
  type: 'select' | 'slider' | 'number' | 'text' | 'toggle' | 'array';
  /**
   * For `type: 'text'` — the value must be an `https://` URL. Enforced lexically here
   * (RN-safe, no URL constructor); the backend connection-write seams additionally run
   * the SSRF blocklist (`validateWebhookUrl`) on these fields. Introduced for the API
   * channel's `webhook_url` — a plaintext `http:` endpoint would ship conversation
   * content in cleartext.
   */
  format?: 'url';
  required?: boolean;
  default?: unknown;
  options?: Array<string | { value: string; label: string; deprecated?: boolean }>;
  /** When true, accept any string value for select fields (not just values in options) */
  allowCustomValue?: boolean;
  min?: number;
  max?: number;
  /**
   * For `type: 'array'` — the element type (default `'text'`). Every element is a
   * string; `'select'` additionally constrains each element to `options` (unless
   * `allowCustomValue`). Introduced for the harness settings surface
   * (`skills[]`, `mcp_servers[]`, `network_egress_allowlist[]`) in PR 2.1.
   */
  itemType?: 'text' | 'select';
  /** For `type: 'array'` — max element count (reject beyond). */
  maxItems?: number;
  /** For `type: 'array'` — min element count (an empty array passes unless set). */
  minItems?: number;
  dependsOn?: { field: string; values: string[] };
}

export type AgentSettingsSchema = Record<string, SchemaField>;

/** Lexical `https://<host>…` guard for `format: 'url'` fields (RN-safe — no URL constructor). */
const HTTPS_URL_LEXICAL = /^https:\/\/[^\s/]+/i;

/**
 * Allowed string values for a select-style field. Shared by the `select` scalar
 * case and the `array` `itemType: 'select'` case — a plain-string option IS the
 * value; an object option carries `{ value }`.
 */
function optionValues(options: SchemaField['options']): string[] {
  return (options ?? []).map((opt) => (typeof opt === 'string' ? opt : opt.value));
}

/**
 * Validates agent_settings against the connector's schema.
 * Returns array of validation errors, or empty array if valid.
 * Use for strict validation paths (MCP, CI).
 */
export function validateAgentSettings(
  settings: Record<string, unknown>,
  schema: AgentSettingsSchema
): string[] {
  const errors: string[] = [];

  // Check required fields
  for (const [fieldName, fieldSchema] of Object.entries(schema)) {
    // Skip validation if field has dependsOn and condition is not met
    if (fieldSchema.dependsOn) {
      const dependsOnValue = settings[fieldSchema.dependsOn.field];
      if (!fieldSchema.dependsOn.values.includes(dependsOnValue as string)) {
        continue; // Field is not applicable for this configuration
      }
    }

    const value = settings[fieldName];

    // Check required
    if (fieldSchema.required && (value === undefined || value === null)) {
      errors.push(`Missing required field: ${fieldName}`);
      continue;
    }

    // Skip further validation if value not provided (and not required)
    if (value === undefined) continue;

    // Validate by type
    switch (fieldSchema.type) {
      case 'select': {
        if (typeof value !== 'string') {
          errors.push(`${fieldName} must be a string`);
        } else if (fieldSchema.options && !fieldSchema.allowCustomValue) {
          const allowedValues = optionValues(fieldSchema.options);
          if (!allowedValues.includes(value)) {
            errors.push(`Invalid value for ${fieldName}: "${value}". Allowed: ${allowedValues.join(', ')}`);
          }
        }
        break;
      }
      case 'slider':
      case 'number': {
        if (typeof value !== 'number') {
          errors.push(`${fieldName} must be a number`);
        } else {
          if (fieldSchema.min !== undefined && value < fieldSchema.min) {
            errors.push(`${fieldName} must be >= ${fieldSchema.min}`);
          }
          if (fieldSchema.max !== undefined && value > fieldSchema.max) {
            errors.push(`${fieldName} must be <= ${fieldSchema.max}`);
          }
        }
        break;
      }
      case 'text': {
        if (typeof value !== 'string') {
          errors.push(`${fieldName} must be a string`);
        } else if (fieldSchema.format === 'url' && !HTTPS_URL_LEXICAL.test(value)) {
          // Lexical (RN-safe) https check; the backend seam runs the full SSRF blocklist.
          errors.push(`${fieldName} must be an https:// URL`);
        }
        break;
      }
      case 'toggle': {
        if (typeof value !== 'boolean') {
          errors.push(`${fieldName} must be a boolean`);
        }
        break;
      }
      case 'array': {
        if (!Array.isArray(value)) {
          errors.push(`${fieldName} must be an array`);
          break;
        }
        if (fieldSchema.maxItems !== undefined && value.length > fieldSchema.maxItems) {
          errors.push(`${fieldName} must have at most ${fieldSchema.maxItems} items`);
        }
        if (fieldSchema.minItems !== undefined && value.length < fieldSchema.minItems) {
          errors.push(`${fieldName} must have at least ${fieldSchema.minItems} items`);
        }
        const itemType = fieldSchema.itemType ?? 'text';
        // Every element must be a string; one message is enough (don't spam per item).
        if (value.some(item => typeof item !== 'string')) {
          errors.push(`${fieldName} items must be strings`);
        } else if (itemType === 'select' && fieldSchema.options && !fieldSchema.allowCustomValue) {
          const allowedValues = optionValues(fieldSchema.options);
          const bad = (value as string[]).find(item => !allowedValues.includes(item));
          if (bad !== undefined) {
            errors.push(`Invalid value in ${fieldName}: "${bad}". Allowed: ${allowedValues.join(', ')}`);
          }
        }
        break;
      }
    }
  }

  // Warn about unknown fields (not in schema)
  for (const fieldName of Object.keys(settings)) {
    if (!(fieldName in schema)) {
      errors.push(`Unknown field: ${fieldName}. This connector does not support this setting.`);
    }
  }

  // Cross-field rule: the system-prompt cache TTL must not be shorter-lived than
  // the conversation-history cache TTL (Anthropic rejects that ordering). Per-field
  // validation above can't express this, so check it here.
  const ttlError = validateCacheTtlOrder(settings);
  if (ttlError) errors.push(ttlError);

  return errors;
}

/**
 * Cross-field guard for Anthropic prompt-cache TTLs.
 *
 * The system-prompt breakpoint (`cache_ttl`) precedes the conversation-history
 * breakpoint (`cache_ttl_history`) in the prompt. Anthropic rejects a request where a
 * longer-lived cache breakpoint follows a shorter-lived one, so the single invalid
 * pairing is system `5m` + history `1h` (`off` removes a breakpoint, so it can never
 * violate the ordering; `1h`+`5m`, `5m`+`5m`, `1h`+`1h` are all fine).
 *
 * Returns an error string when the pairing is invalid, else `null`. Connector-agnostic:
 * only fires when BOTH keys carry the offending values, so it is a no-op for connectors
 * (OpenAI/Gemini/etc.) that never set these fields.
 *
 * NOTE: the runtime clamp in `resolveCacheTtls`
 * (apps/backend/src/llm/providers/cache-control.ts) REUSES this function to detect the bad
 * pairing and clamps it defensively for already-persisted rows — one source of truth.
 */
export function validateCacheTtlOrder(settings: Record<string, unknown>): string | null {
  if (settings.cache_ttl === '5m' && settings.cache_ttl_history === '1h') {
    return 'System Prompt Cache TTL (5m) must be ≥ Conversation History Cache TTL (1h): a longer-lived cache breakpoint cannot follow a shorter-lived one.';
  }
  return null;
}

/**
 * Result of sanitizing agent settings
 */
export interface SanitizeResult {
  /** Settings object with only schema-defined keys */
  sanitized: Record<string, unknown>;
  /** Keys that were stripped (not in schema) */
  strippedKeys: string[];
}

/**
 * Sanitizes agent_settings against the connector's schema.
 * Strips unknown keys not defined in the schema.
 * Use for lenient paths (REST API, CI, frontend).
 */
export function sanitizeAgentSettings(
  settings: Record<string, unknown>,
  schema: AgentSettingsSchema
): SanitizeResult {
  const sanitized: Record<string, unknown> = {};
  const strippedKeys: string[] = [];

  // Copy only schema-defined keys
  for (const key of Object.keys(settings)) {
    if (key in schema) {
      sanitized[key] = settings[key];
    } else {
      strippedKeys.push(key);
    }
  }

  return { sanitized, strippedKeys };
}
